import { useMemo } from 'react';
import { useIsSbsMode } from '../useIsSbs';
import { useTranslate } from '@tolgee/react';
import { isTiKTok } from 'lib/utils/isTikTok';

const NOT_SUPPORT_SEND_CHANNEL: Conversation.Conversation['channel'][] = ['MERCADO', 'TOKOPEDIA'];
/** 是否支持发送商品 */
export const useIsSupportSendProduct = (channel: Conversation.Conversation['channel']) => {
  const isSbsMode = useIsSbsMode();
  // 是否支持发送商品
  const supportSend = useMemo(() => {
    return !isSbsMode && !NOT_SUPPORT_SEND_CHANNEL.includes(channel);
  }, [channel, isSbsMode]);

  return supportSend;
};

export type ChatSideTabKey = 'trade' | 'product' | 'voucher' | 'todo' | 'buyer';

const CHANNEL_BASE_TAB: Record<General.PlatformUpperCaseTunnel, ChatSideTabKey[]> = {
  SHOPEE: ['trade', 'product', 'voucher'],
  LAZADA: ['trade', 'product', 'voucher'],
  ALIEXPRESS: ['trade', 'product', 'voucher'],
  TOKOPEDIA: ['trade', 'product'],
  TIKTOK: ['trade', 'product', 'voucher'],
  TIKTOK_US: ['trade', 'product', 'voucher'],
  WA_BIZ: ['trade'],
  MERCADO: ['trade'],
  KWAI: ['trade', 'product', 'voucher'],
  FACEBOOK: ['buyer'],
};

export const getChannelBaseTab = (channel: General.PlatformUpperCaseTunnel) => {
  return CHANNEL_BASE_TAB[channel] || [];
};

export const useIsSupportProduct = (channel: General.PlatformUpperCaseTunnel) => {
  const isSupport = useMemo(() => {
    return CHANNEL_BASE_TAB[channel]?.includes('product') || false;
  }, [channel]);

  return isSupport;
};

export const useIsSupportVoucher = (channel: General.PlatformUpperCaseTunnel) => {
  const isSupport = useMemo(() => {
    return CHANNEL_BASE_TAB[channel]?.includes('voucher') || false;
  }, [channel]);

  return isSupport;
};

const SUPPORT_SEND_VOUCHER_PLATFORM: General.PlatformUpperCaseTunnel[] = [
  'LAZADA',
  'SHOPEE',
  'TIKTOK',
  'TIKTOK_US',
];

export const useIsSupportSendVoucher = (channel: Conversation.Conversation['channel']) => {
  const isSupport = useMemo(() => {
    return SUPPORT_SEND_VOUCHER_PLATFORM.includes(channel) || false;
  }, [channel]);
  return isSupport;
};

interface UseSendVoucherDisabledParams {
  channel: Conversation.Conversation['channel'];
  status: Conversation.Voucher.Vo['voucherStatus'];
  suspendFlag: boolean;
  isTiktok: boolean;
  currentUsage: number;
  usageQuantity: number;
  controlStatus: boolean;
  isAffiliateSip?: boolean;
}
export const useSendVoucherDisabled = (params: UseSendVoucherDisabledParams) => {
  const {
    channel,
    status,
    suspendFlag,
    isTiktok,
    currentUsage,
    usageQuantity,
    controlStatus,
    isAffiliateSip,
  } = params;
  const { t } = useTranslate();
  const lazadaSendVoucherDisbaled = useMemo(() => {
    // lazada 非进行中状态的优惠券均不可发送
    return channel === 'LAZADA' && !(status === 'ONGOING' && !suspendFlag);
  }, [channel, status, suspendFlag]);

  const tiktokSendVoucherDisabled = useMemo(() => {
    return isTiktok && (usageQuantity > 0 ? currentUsage >= usageQuantity : false);
  }, [isTiktok, currentUsage, usageQuantity]);

  const sendVoucherDisabled = useMemo(() => {
    return isTiktok
      ? tiktokSendVoucherDisabled
      : controlStatus === false ||
          isAffiliateSip ||
          currentUsage >= usageQuantity ||
          lazadaSendVoucherDisbaled;
  }, [
    isTiktok,
    controlStatus,
    currentUsage,
    isAffiliateSip,
    lazadaSendVoucherDisbaled,
    usageQuantity,
    tiktokSendVoucherDisabled,
  ]);

  const sendDisabledTips = useMemo(() => {
    if (lazadaSendVoucherDisbaled) {
      if (status === 'SCHEDULED') {
        return t('voucher.sendDisabledTips1');
      }
      if (status === 'ONGOING' && suspendFlag) {
        return t('voucher.sendDisabledTips2');
      }
    }
    if (currentUsage >= usageQuantity) {
      return t('优惠券已领完');
    }
    return '';
  }, [currentUsage, lazadaSendVoucherDisbaled, status, suspendFlag, t, usageQuantity]);

  return {
    sendVoucherDisabled,
    lazadaSendVoucherDisbaled,
    tiktokSendVoucherDisabled,
    sendDisabledTips,
  };
};

type VoucherSearchOption = {
  label: string;
  value: Conversation.Voucher.SearchKeywordType;
};
export const useVoucherSearchOptions = (channel: Conversation.Conversation['channel']) => {
  const { t } = useTranslate();
  const options = useMemo(() => {
    const base = [
      {
        label: t('searchOption.name'),
        value: 0,
      },
      {
        label: t('searchOption.discount'),
        value: 1,
      },
      {
        label: t('searchOption.discountPercent'),
        value: 2,
      },
    ] as VoucherSearchOption[];
    if (isTiKTok(channel)) {
      return base;
    }
    return [
      ...base,
      {
        label: t('searchOption.code'),
        value: 3,
      },
    ] as VoucherSearchOption[];
  }, [channel, t]);
  return options;
};
